import { Feather } from '@expo/vector-icons';
import { MeetingLinksType, MeetingsModel } from '@w3lcome/types';
import { Text } from '_/components';
import { fontFamily } from '_/config/theme';
import themes from '_/constants/themes';
import { MeetingGuest } from '_/hooks/MeetingsProvider';
import { meetingLinksApi } from '_/services/api';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Clipboard,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
  Share,
} from 'react-native';
import { showMessage } from 'react-native-flash-message';
import { useSelector } from 'react-redux';
import { appConfig } from '_/config/app';

export const GUEST_LINKS_QUERY_KEY = (meetingId: string) => ['meetingGuestLinks', meetingId];

interface GuestWithLink extends MeetingGuest {
  linkId?: string;
  linkUrl?: string;
}

export function buildGuestLink(linkId: string, guest: MeetingGuest): string {
  const base = appConfig.preCheckinURL ?? '';
  if (guest.email) return `${base}/precheckin/${linkId}?type=link&email=${btoa(guest.email)}`;
  if (guest.phone) {
    const nameQ = guest.name ? `&name=${btoa(guest.name)}` : '';
    return `${base}/precheckin/${linkId}?type=link&phone=${btoa(guest.phone)}${nameQ}`;
  }
  if (guest.idNumber) {
    const nameQ = guest.name ? `&name=${btoa(guest.name)}` : '';
    return `${base}/precheckin/${linkId}?type=link&idNumber=${btoa(guest.idNumber)}${nameQ}`;
  }
  return '';
}

// ── GuestRow ──────────────────────────────────────────────────────────────────

function GuestRow({ guest }: { guest: GuestWithLink }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const identifier =
    guest.email || guest.phone || (guest.idNumber ? `${t('Document')}: ${guest.idNumber}` : '—');
  const label = guest.name ? `${guest.name} — ${identifier}` : identifier;

  function handleCopy() {
    if (!guest.linkUrl) return;
    if (Platform.OS === 'web') {
      navigator.clipboard?.writeText(guest.linkUrl);
    } else {
      Clipboard.setString(guest.linkUrl);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleShare() {
    if (!guest.linkUrl) return;
    try {
      await Share.share({ message: guest.linkUrl });
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <View style={rowStyles.container}>
      <Text style={rowStyles.label} numberOfLines={1}>
        {label}
      </Text>
      {guest.linkUrl ? (
        <View style={rowStyles.actions}>
          <TouchableOpacity onPress={handleCopy} style={rowStyles.actionBtn}>
            <Feather
              name={copied ? 'check' : 'copy'}
              size={20}
              color={copied ? themes.colors.success.toString() : themes.colors.primary}
            />
            {/* <Text style={[rowStyles.copyText, copied && rowStyles.copyTextSuccess]}>
              {copied ? t('Copied') : t('Copy')}
            </Text> */}
          </TouchableOpacity>
          <TouchableOpacity onPress={handleShare} style={rowStyles.actionBtn}>
            <Feather name="send" size={20} color={themes.colors.primary} />
          </TouchableOpacity>
        </View>
      ) : (
        <Text style={rowStyles.pendingText}>{t('linkAvailableSoon')} (~5 min)</Text>
      )}
    </View>
  );
}

const rowStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: themes.colors.lightSecondary,
  },
  label: {
    flex: 1,
    color: themes.colors.darkQuaternary,
    fontFamily: fontFamily.regular,
    fontSize: 12,
    marginRight: 8,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  copyText: { color: themes.colors.primary, fontFamily: fontFamily.regular, fontSize: 12 },
  copyTextSuccess: { color: themes.colors.success.toString() },
  pendingText: { color: themes.colors.graySecondary, fontFamily: fontFamily.regular, fontSize: 11 },
});

// ── GuestList ─────────────────────────────────────────────────────────────────

interface GuestListProps {
  meeting: MeetingsModel;
  isCredentialing?: boolean;
}

export default function GuestList({ meeting, isCredentialing }: GuestListProps) {
  const { t } = useTranslation();
  const host = useSelector((state: any) => state.host);
  const queryClient = useQueryClient();

  const [expanded, setExpanded] = useState(false);
  const [guests, setGuests] = useState<GuestWithLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const shouldShowForHost = meeting?.hostId === host?.id || meeting?.coHosts?.includes(host?.email);

  const getCached = () =>
    queryClient.getQueryData<GuestWithLink[]>(GUEST_LINKS_QUERY_KEY(meeting?.id));

  const fetchGuests = useCallback(async () => {
    if (!meeting?.id || loading) return;
    setLoading(true);
    try {
      const { data } = await meetingLinksApi.getLinksByMeeting(meeting.id, MeetingLinksType.GUEST, {
        $limit: 500,
        $skip: 0,
      });

      const normalized: GuestWithLink[] = data
        .filter((l: any) => l.email || l.phone || l.idNumber)
        .map((l: any) => {
          const g: MeetingGuest = {
            name: l.name || undefined,
            email: l.email || undefined,
            phone: l.phone || undefined,
            idNumber: l.idNumber || undefined,
          };
          return { ...g, linkId: l.id, linkUrl: buildGuestLink(l.id, g) };
        });

      setGuests(normalized);
      queryClient.setQueryData(GUEST_LINKS_QUERY_KEY(meeting.id), normalized);
      setLoaded(true);
    } catch {
      showMessage({ message: t('GuestList.error'), type: 'danger' });
    } finally {
      setLoading(false);
    }
  }, [meeting?.id]);

  useEffect(() => {
    if (!expanded) return;
    const cached = getCached();

    const needsFetch = !cached || cached.length === 0 || cached.some((g) => !g.linkUrl);

    if (!needsFetch) {
      setGuests(cached!);
      setLoaded(true);
    } else {
      if (cached && cached.length > 0) setGuests(cached);
      setLoaded(false);
      fetchGuests();
    }
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return;
    const cached = getCached();
    if (cached) {
      setGuests(cached);
      setLoaded(true);
    }
  }, [queryClient.getQueryState(GUEST_LINKS_QUERY_KEY(meeting?.id))?.dataUpdatedAt]);

  if (!shouldShowForHost) return null;

  return (
    <View style={styles.container}>
      <TouchableOpacity style={styles.rowContainer} onPress={() => setExpanded((prev) => !prev)}>
        <Text style={styles.title}>
          {t(isCredentialing ? 'accreditedGuests' : 'meeting.guests')}
          {guests.length > 0 ? ` (${guests.length})` : ''}
        </Text>
        <Feather name={expanded ? 'chevron-up' : 'chevron-down'} style={styles.icons} />
      </TouchableOpacity>

      {expanded && (
        <>
          {loading && (
            <View style={styles.loadingRow}>
              <Text style={styles.title}>{t('meeting.loadingInvitees')}</Text>
              <ActivityIndicator size="small" color={themes.colors.primary} />
            </View>
          )}
          {!loading && guests.length === 0 && (
            <Text style={styles.empty}>{t('meeting.emptyInvitees')}</Text>
          )}
          {!loading &&
            guests.map((g, idx) => (
              <GuestRow key={g.email || g.phone || g.idNumber || idx} guest={g} />
            ))}
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, marginBottom: 8 },
  rowContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  icons: { fontSize: 20, color: themes.colors.primary },
  title: { color: themes.colors.darkSecondary, fontFamily: fontFamily.regular, fontSize: 12 },
  empty: {
    color: themes.colors.graySecondary,
    fontFamily: fontFamily.regular,
    fontSize: 12,
    paddingVertical: 8,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
  },
});
